import { periodName } from './model.js'

// The game clock as the fun layer reasons about it. P6-330b (GN-3b).
//
// Pure functions over `{ period, clock }`: how many seconds of GAME TIME have gone, how old a callout is
// against a card, and the words a callout is shown in. Nothing here reads the wall clock: the design's
// four-minute window is a window of game time, so a callout from the end of one quarter is still "recent"
// at the start of the next, and a stoppage of an hour does not age it by a second.
//
// ═══════════════════════════════════════════════════════════════════════════
// A PERIOD IS NOT ALWAYS TWELVE MINUTES
// ═══════════════════════════════════════════════════════════════════════════
//
// The four quarters are 12 minutes and every overtime (period 5 and up) is 5. The design's own `absT` says so
// (`D2D Hoops v1.dc.html`: `p <= 4 ? (p - 1) * 720 + 720 - c : 2880 + (p - 5) * 300 + 300 - c`), and the
// producer's contract repeats it (`docs/reports/P6-330a.md`, concern 12): *"a callout from the end of one
// period and a card in the next needs both lengths"*. A page that used 720 for every period would work for
// the whole of regulation and then hide a callout in overtime for no reason: the card's overtime clock would
// read as minutes from a quarter that never had them.

/** The four quarters, in game seconds. */
export const QUARTER_SECONDS = 720
/** Each overtime, in game seconds. */
export const OVERTIME_SECONDS = 300
/** Design flag 9: a callout is shown while it is within four minutes of game clock, and not after. */
export const CALLOUT_WINDOW_SECONDS = 240

/** A period's length in game seconds: 720 for the four quarters, 300 for an overtime, `null` for a non-period. */
export function periodSeconds(period) {
  if (!Number.isInteger(period) || period < 1) return null
  return period <= 4 ? QUARTER_SECONDS : OVERTIME_SECONDS
}

/** `M:SS` as the seconds left on the clock, or `null` for anything else (the producer writes the feed's form). */
export function clockSeconds(clock) {
  const match = typeof clock === 'string' ? /^(\d{1,2}):(\d{2})$/.exec(clock) : null
  if (match === null) return null
  const seconds = Number(match[2])
  return seconds > 59 ? null : Number(match[1]) * 60 + seconds
}

/**
 * Game seconds that have gone when `period` shows `clock`: every earlier period whole, plus what has gone in
 * this one. `null` for a period or a clock that cannot be read, and for a clock that has MORE time on it than
 * the period has (a clock that is not this period's).
 */
export function elapsedAt(period, clock) {
  const length = periodSeconds(period)
  const left = clockSeconds(clock)
  if (length === null || left === null || left > length) return null
  const before = period <= 4 ? (period - 1) * QUARTER_SECONDS : 4 * QUARTER_SECONDS + (period - 5) * OVERTIME_SECONDS
  return before + (length - left)
}

/**
 * Game seconds that have gone on a CARD, from its own `period` and `clock`.
 *
 * A `null` clock is the period RUN OUT: the feed's blank between quarters, which the producer publishes as
 * `null` (`model.js::endOfPeriod` reads it the same way, and the status line says `End of Q1` for it). An
 * ABSENT clock (`undefined`) is not that: it is a card that did not say, and the answer is `null`.
 */
export function cardElapsed(card) {
  if (card?.clock === null) return elapsedAt(card.period, '0:00')
  return elapsedAt(card?.period, card?.clock)
}

/**
 * How old a callout is against a card, in game seconds, or `null` when either clock cannot be read.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * A NEGATIVE AGE IS ZERO, NOT AN ERROR
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * The callout comes from the play-by-play file and the card's clock from the box score, two files fetched in the
 * same tick, so the callout can be a few seconds AHEAD of the card (the producer says so in as many words,
 * `docs/reports/P6-330a.md` concern 12). That is a callout from the present, not a callout from the future, and
 * it is as fresh as a callout can be.
 */
export function calloutAge(callout, card) {
  const was = elapsedAt(callout?.period, callout?.clock)
  const now = cardElapsed(card)
  if (was === null || now === null) return null
  return Math.max(0, now - was)
}

/** Is this the five-key object the producer writes? The page reads `text` and `period` and `clock` and `team`. */
export function isCallout(callout) {
  return (
    callout !== null &&
    typeof callout === 'object' &&
    typeof callout.text === 'string' &&
    callout.text.trim() !== '' &&
    Number.isInteger(callout.period)
  )
}

const capitalised = (text) => `${text.charAt(0).toUpperCase()}${text.slice(1)}`

/**
 * The words a callout is shown in.
 *
 * `run` and `biggest_lead` carry their own figure and read as the design writes them with the team in front
 * (`MIA 8–0 run`, `HOU biggest lead, 12`). A `lead_change` is the producer's two words (`lead change`) and the
 * team that TOOK the lead, so the team goes after it: `Lead change, HOU`. (The design says `Lead change, HOU up
 * 3`; the margin is not published, and a margin is not invented to match the drawing.)
 *
 * ⚠️ NO KIND IS ASSUMED: one this page has never seen is the team and the producer's own text, which is a
 * sentence either way.
 */
export function calloutLine(callout) {
  const text = String(callout.text).trim()
  const team = typeof callout.team === 'string' ? callout.team.trim() : ''
  if (callout.kind === 'lead_change') return team === '' ? capitalised(text) : `${capitalised(text)}, ${team}`
  return team === '' ? text : `${team} ${text}`
}

/** `Q3 4:20`: the period and the clock of the play the callout was last true on. */
export function calloutWhen(callout) {
  const name = periodName(callout.period)
  const clock = typeof callout.clock === 'string' ? callout.clock : ''
  if (name === null) return clock
  return clock === '' ? name : `${name} ${clock}`
}
